
import { CrudEndpoints } from 'types/common';
import { createGenericCRUD, useUploadFileData } from './commonCrud';
import useSWR, { mutate } from 'swr';
import { CurrentUser, GoogleAuthSetupRequest, GoogleAuthSetupResponse } from 'types/currentUser';
import { fetcher, fetcherPut, fetcherWithMethod } from 'utils/axios';
import { handleApiError, showSuccess } from 'utils/errorHandler';

export const endpoints: CrudEndpoints = {
  key: 'UserManagement/CurrentUser',
  getList: '/GetList',
  getListById: '/GetList/{ID}',
  getModal: '/',
  getLookupList: '/GetLookupList',
  insert: '/',
  update: '/',
  delete: ''
};

// Create CRUD operations for CurrentUser
const currentUserCRUD = createGenericCRUD<CurrentUser>(endpoints);

export function useGetCurrentUserModal() {
  const response = useSWR<string>(`UserManagement/CurrentUser`, fetcher, {
    revalidateIfStale: true,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    onError: (err) => {
      handleApiError(err);
    }
  });
  return {
    ...response,
    data: response.data ?? null
  };
}

export function useGetProfilePicture() {
  const response = useSWR<string>(`UserManagement/CurrentUser/GetUserProfileImageUrl`, fetcher, {
    revalidateIfStale: true,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    onError: (err) => {
      handleApiError(err);
    }
  });
  return {
    ...response,
    data: response.data ?? null
  };
}

export function useGetProfilePictureThumbnail() {
  const response = useSWR<string>(`UserManagement/CurrentUser/GetUserProfileImageThumbnailUrl`, fetcher, {
    revalidateIfStale: true,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    onError: (err) => {
      handleApiError(err);
    }
  });
  return {
    ...response,
    data: response.data ?? null
  };
}

// Update profile picture
export async function useUpdateCurrentUserProfilePicture(file: File | null, message?: string): Promise<CurrentUser | null> {
  const result = await useUploadFileData<CurrentUser>(`UserManagement/CurrentUser/updateUserProfileImage`,
    null,
    {
      FileName: file,
      message: message
    },
    { showSnackbar: false, method: 'POST' }
  );
  if (result) {
    // Force revalidation of profile picture URLs
    await mutate('UserManagement/CurrentUser/GetUserProfileImageUrl', undefined, { revalidate: true });
    await mutate('UserManagement/CurrentUser/GetUserProfileImageThumbnailUrl', undefined, { revalidate: true });
    await mutate('UserManagement/CurrentUser', undefined, { revalidate: true });
  }
  return result;
}

// Seal APIs for CurrentUser
export function useGetSeal() {
  const response = useSWR<string>(`${endpoints.key}/GetSeal`, fetcher, {
    revalidateIfStale: true,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    onError: (err) => {
      handleApiError(err);
    }
  });
  return {
    ...response,
    data: response.data ?? null
  };
}

export const useGetCurrentUserSeal = useGetSeal;

export async function useUpdateSeal(file: File | null, message?: string): Promise<any | null> {
  const result = await useUploadFileData<any>(
    `${endpoints.key}/UpdateSeal`,
    null,
    {
      sealFile: file
    },
    { showSnackbar: Boolean(message), method: 'PUT' }
  );

  if (file === null) {
    mutate(`${endpoints.key}/GetSeal`, '', false);
  }

  await mutate(`${endpoints.key}/GetSeal`);

  return result;
}

export const useUpdateCurrentUserSeal = useUpdateSeal;

// Signature APIs for CurrentUser
export function useGetSignature() {
  const response = useSWR<string>(`${endpoints.key}/GetSignature`, fetcher, {
    revalidateIfStale: true,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    onError: (err) => {
      handleApiError(err);
    }
  });
  return {
    ...response,
    data: response.data ?? null
  };
}

export const useGetCurrentUserSignature = useGetSignature;

export async function useUpdateSignature(file: File | null, message?: string): Promise<any | null> {
  const result = await useUploadFileData<any>(
    `${endpoints.key}/UpdateSignature`,
    null,
    {
      signatureFile: file
    },
    { showSnackbar: Boolean(message), method: 'PUT' }
  );

  if (file === null) {
    mutate(`${endpoints.key}/GetSignature`, '', false);
  }

  await mutate(`${endpoints.key}/GetSignature`);

  return result;
}

export const useUpdateCurrentUserSignature = useUpdateSignature;

// License Number APIs for CurrentUser
export const useUpdateLicenseNo = async (data: { licenseNo: string; updatedOn: string | null }) => {
  try {
    const payload = {
      updatedOn: data.updatedOn,
      licenseNo: data.licenseNo
    };
    const response = await fetcherWithMethod('PUT', [`${endpoints.key}/UpdateLicenseNo`, { data: payload }]);
    await mutate('UserManagement/CurrentUser');
    return response;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const useUpdateCurrentUserLicenseNo = useUpdateLicenseNo;




export async function useUpdateCurrentUser<T = any>(data: any): Promise<T | null> {
  try {
    const result = await fetcherPut([`UserManagement/CurrentUser`, { data }]);
    if (result) {
      await mutate('UserManagement/CurrentUser');
      showSuccess('Updated successfully');
      return result;
    }
    return result;
  } catch (error) {
    handleApiError(error);
    return null;
  }
}

export async function useGenerateGoogleAuthenticatorSetupCode(payload?: GoogleAuthSetupRequest): Promise<GoogleAuthSetupResponse | null> {
  try {
    const response = await fetcherWithMethod('POST', [`${endpoints.key}/GenerateGoogleAuthenticatorSetupCode`, { data: payload }]);
    return response as GoogleAuthSetupResponse;
  } catch (err) {
    handleApiError(err);
    return null;
  }
}

export async function useGetGoogleAuthQrCode(): Promise<any | null> {
  try {
    const response = await fetcherWithMethod(
      'GET',
      `${endpoints.key}/GetGoogleAuthenticatorQrCode`
    );
    return response as any;
  } catch (err) {
    handleApiError(err);
    return null;
  }
}

export const {
  useGetLookupList: useGetCurrentUserLookupList,
  useInsertRecord: useInsertCurrentUserRecord,
  useDeleteRecord: useDeleteCurrentUserRecord
} = currentUserCRUD;
